/**
 * Slash-opdrachten in het invoerveld (opdracht Frank 24 oktober 2026). PUUR: alleen de catalogus
 * en het filteren; wat een opdracht DOET, hangt ChatArea eraan (`run`).
 *
 * Deel 1: de lijst, het filteren en /help, op de opdrachten waarvan de functie al bestaat
 * (Openen en Gesprek). Regel 7 van Frank: een opdracht waarvan de functie er nog niet is, staat
 * NIET in de lijst; die komt pas mee in het deel waarin hij wordt gebouwd.
 *
 * Elke opdracht heeft een Nederlandse en een Engelse naam; beide werken in beide talen
 * (/agenten en /agents), het menu toont de naam van de taal van de app.
 */

/** De groepen in de volgorde van het menu. De tekst is Nederlands; het scherm vertaalt. */
export const GROEPEN = ["Maken", "Bronnen lezen", "Doen", "Bewerken", "Openen", "Gesprek", "Veiligheid", "Hulp"];

export const OPDRACHTEN = [
  // ── Maken (deel 2) ─────────────────────────────────────────────────────
  // Via de bestaande voorstelkaart van een digitale worker: er ontstaat niets zonder Bevestig.
  { id: "worker", nl: "worker", en: "worker", groep: "Maken", metTekst: true,
    uitleg: "Maakt een voorstel voor een digitale worker: wat, wanneer, welke bronnen en een korte naam",
    voorbeeld: "/worker vat elke maandag om 08:00 mijn TASQ-taken samen" },
  { id: "agent", nl: "agent", en: "agent", groep: "Maken", metTekst: true,
    // GL213-6 (besluit Frank 28 oktober 2026): SL-b vervalt; een genoemd tijdstip gaat mee.
    uitleg: "Maakt een voorstel voor een agent; noem je een tijdstip, dan neemt de kaart het over",
    voorbeeld: "/agent zoek offertes in mijn mail" },
  // SL-a (Frank 24 okt): het bestaande formulier opent met de naam ingevuld; de klik op Aanmaken is de
  // bevestiging. /document staat er niet bij: daar is nog geen formulier (regel 7, post NS-D3).
  { id: "project", nl: "project", en: "project", groep: "Maken", metTekst: true, formulier: true,
    uitleg: "Opent het formulier voor een nieuw project, met de naam ingevuld", voorbeeld: "/project Chalet Check" },
  { id: "kennisbank", nl: "kennisbank", en: "knowledgebase", groep: "Maken", metTekst: true, formulier: true,
    uitleg: "Opent het formulier voor een nieuwe kennisbank, met de naam ingevuld", voorbeeld: "/kennisbank Offertes" },
  { id: "team", nl: "team", en: "team", groep: "Maken", metTekst: true, formulier: true,
    uitleg: "Opent het formulier voor een nieuw team, met de naam ingevuld", voorbeeld: "/team Uganda" },
  { id: "skill", nl: "skill", en: "skill", groep: "Maken", metTekst: true, formulier: true,
    uitleg: "Opent het formulier voor een nieuwe skill, met de naam ingevuld", voorbeeld: "/skill offerte nakijken" },
  // ── Bronnen lezen (deel 3) ─────────────────────────────────────────────
  // Lezen gaat direct (regel 3): de opdracht wordt een vraag in de chat, langs de gewone route.
  { id: "taken", nl: "taken", en: "tasks", groep: "Bronnen lezen", metTekst: true,
    uitleg: "Toont je open TASQ-taken, alle statussen behalve afgerond", voorbeeld: "/taken over het bouwplan" },
  { id: "mail", nl: "mail", en: "mail", groep: "Bronnen lezen", metTekst: true,
    uitleg: "Toont je mail van vandaag, of van de periode die je erachter zet", voorbeeld: "/mail gisteren" },
  // SL-c (25 okt 2026): dezelfde vorm als /taken en /mail; de zin noemt de bron bij naam.
  { id: "agenda", nl: "agenda", en: "calendar", groep: "Bronnen lezen", metTekst: true,
    uitleg: "Toont je agenda van vandaag, of van de dag die je erachter zet", voorbeeld: "/agenda morgen" },
  { id: "meetings", nl: "meetings", en: "meetings", groep: "Bronnen lezen", metTekst: true,
    uitleg: "Toont je recente gespreksverslagen uit Fathom", voorbeeld: "/meetings over de offerte" },
  { id: "drive", nl: "drive", en: "drive", groep: "Bronnen lezen", metTekst: true,
    uitleg: "Toont je recente bestanden in Google Drive", voorbeeld: "/drive over het bouwplan" },
  // ── Openen ─────────────────────────────────────────────────────────────
  { id: "universum", nl: "universum", en: "universe", groep: "Openen",
    uitleg: "Opent het Universum: alle verbindingen als grafiek", voorbeeld: "/universum" },
  { id: "logboek", nl: "logboek", en: "log", groep: "Openen",
    uitleg: "Opent het Logboek met alle handelingen", voorbeeld: "/logboek" },
  { id: "postbus", nl: "postbus", en: "inbox", groep: "Openen",
    uitleg: "Opent de Postbus met de ochtendronde en voorstellen", voorbeeld: "/postbus" },
  { id: "geheugen", nl: "geheugen", en: "memory", groep: "Openen",
    uitleg: "Opent het Geheugen", voorbeeld: "/geheugen" },
  { id: "koppelingen", nl: "koppelingen", en: "connections", groep: "Openen",
    uitleg: "Opent Koppelingen met je eigen koppelingen en Ontdekken", voorbeeld: "/koppelingen" },
  { id: "skills", nl: "skills", en: "skills", groep: "Openen",
    uitleg: "Opent het scherm Skills", voorbeeld: "/skills" },
  { id: "agenten", nl: "agenten", en: "agents", groep: "Openen",
    uitleg: "Opent Agenten met je agents en workers", voorbeeld: "/agenten" },
  { id: "instellingen", nl: "instellingen", en: "settings", groep: "Openen",
    uitleg: "Opent Instellingen", voorbeeld: "/instellingen" },
  { id: "tijdmachine", nl: "tijdmachine", en: "timemachine", groep: "Openen",
    uitleg: "Opent de Tijdmachine: terug naar een eerder moment", voorbeeld: "/tijdmachine" },
  // ── Gesprek ────────────────────────────────────────────────────────────
  { id: "nieuw", nl: "nieuw", en: "new", groep: "Gesprek",
    uitleg: "Begint een nieuw gesprek", voorbeeld: "/nieuw" },
  { id: "model", nl: "model", en: "model", groep: "Gesprek",
    uitleg: "Kiest het model voor dit gesprek", voorbeeld: "/model", submenu: true },
  { id: "chats", nl: "chats", en: "chats", groep: "Gesprek",
    uitleg: "Zoekt een eerder gesprek", voorbeeld: "/chats" },
  { id: "orkestratie", nl: "orkestratie", en: "orchestrate", groep: "Gesprek",
    uitleg: "Zet orkestratie aan of uit", voorbeeld: "/orkestratie" },
  { id: "raad", nl: "raad", en: "council", groep: "Gesprek",
    uitleg: "Kiest de LLM-raad: synthese, consensus of beste van N", voorbeeld: "/raad", submenu: true },
  // ── Hulp ───────────────────────────────────────────────────────────────
  { id: "help", nl: "help", en: "help", groep: "Hulp",
    uitleg: "Toont alle opdrachten met uitleg en een voorbeeld", voorbeeld: "/help" },
];

/** De naam van een opdracht in de taal van de app. PUUR. */
export function naam(opdracht, taal) {
  return taal === "en" ? opdracht.en : opdracht.nl;
}

/** Het voorbeeld in de taal van de app: "/agenten" wordt "/agents". PUUR. */
export function voorbeeld(opdracht, taal) {
  return taal === "en" ? opdracht.voorbeeld.replace(`/${opdracht.nl}`, `/${opdracht.en}`) : opdracht.voorbeeld;
}

/**
 * De opdrachten die passen bij wat er na de slash staat, in de volgorde van de groepen. PUUR.
 * Een naam in EEN van beide talen die met de tekst begint, telt eerst; daarna de opdrachten
 * waarvan de uitleg de tekst bevat (`vertaal` geeft de uitleg in de taal van de app).
 */
export function passend(tekst, taal = "nl", vertaal = (s) => s) {
  const q = (tekst || "").replace(/^\//, "").trim().toLowerCase();
  const opVolgorde = [...OPDRACHTEN].sort((a, b) => GROEPEN.indexOf(a.groep) - GROEPEN.indexOf(b.groep));
  if (!q) return opVolgorde;
  const opNaam = opVolgorde.filter((o) => o.nl.startsWith(q) || o.en.startsWith(q));
  // De uitleg pas vanaf drie letters: "ag" zit in "vandaag", en dan staat /mail onder /agenten.
  const opUitleg = q.length < 3 ? [] : opVolgorde.filter((o) => !opNaam.includes(o) && vertaal(o.uitleg).toLowerCase().includes(q));
  return [...opNaam, ...opUitleg];
}

/** De opdracht die precies zo heet, in een van beide talen, of null. PUUR. */
export function precies(tekst) {
  const q = (tekst || "").replace(/^\//, "").trim().toLowerCase();
  return OPDRACHTEN.find((o) => o.nl === q || o.en === q) || null;
}

/** De lijst per groep, voor /help. PUUR. */
export function perGroep(opdrachten = OPDRACHTEN) {
  return GROEPEN.map((g) => ({ groep: g, opdrachten: opdrachten.filter((o) => o.groep === g) }))
    .filter((g) => g.opdrachten.length > 0);
}


/** SL-a: de gebeurtenis waarmee /project het projectvenster in de zijbalk opent (geen route: App.js
 *  is bevroren, en het venster bestaat al in `Sidebar`). */
export const NIEUW_PROJECT = "novamund:nieuw-project";

/** SL-a: waar het formulier van een opdracht staat, met de naam erbij, of null. PUUR. */
export function formulierVan(id, naam = "") {
  const n = (naam || "").trim();
  if (id === "project") return { gebeurtenis: NIEUW_PROJECT, naam: n };
  const pad = { kennisbank: "/knowledge", team: "/teams", skill: "/skills" }[id];
  return pad ? { pad, naam: n } : null;
}

/** SL-a: de naam die een pagina via de navigatie meekreeg om het formulier mee te openen, of null. PUUR. */
export function nieuwUitStaat(staat) {
  return staat && typeof staat.nieuw === "string" ? staat.nieuw : null;
}

/** De dagbronnen die het formulier van /worker en /agent aanbiedt, met het woord dat de
 *  workerpoort herkent (`workerzin.BRONWOORDEN`). */
export const WORKERBRONNEN = [
  { id: "tasq", woord: "TASQ" }, { id: "fathom", woord: "Fathom" }, { id: "agenda", woord: "agenda" },
  { id: "mail", woord: "mail" }, { id: "drive", woord: "Google Drive" },
];
const DAGNAAM = {
  nl: ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"],
  en: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
};

/** "/worker iets" of "/agents iets": de opdracht en de tekst erachter, of null. PUUR. */
export function metTekst(invoer) {
  const m = /^\/(\S+)\s+([\s\S]+)$/.exec((invoer || "").trim());
  if (!m) return null;
  const o = precies(m[1]);
  return o && o.metTekst ? { opdracht: o, tekst: m[2].trim() } : null;
}

/** GL213-4: tekst die zelf al "maak een agent/worker die" of "make an agent/worker that" is. */
const ZELF_AL_EEN_OPDRACHT = /^\s*(?:maak|make|create|bouw|build)\s+(?:een|an?)\s+(?:ai[-\s]?)?(?:agent|worker|digitale worker|digital worker)\b/i;

/** De zin die de chat krijgt voor /worker of /agent met tekst erachter. PUUR. */
export function zinMetTekst(id, tekst, taal = "nl") {
  const en = taal === "en";
  // GL213-4 (glasronde 0.9.213): "/agent maak een agent die ..." werd "maak een agent die maak een
  // agent die ...". Begint de tekst al met de vorm, dan gaat hij zoals hij is.
  if ((id === "agent" || id === "worker") && ZELF_AL_EEN_OPDRACHT.test(tekst || "")) return tekst.trim();
  if (id === "taken") return zinZonderTekst(id, taal) + (tekst ? (en ? ` about ${tekst}` : ` over ${tekst}`) : "");
  // De periode gaat achter de vraag; de dagweg leest "gisteren" of "de afgelopen drie dagen".
  if (id === "mail") return en ? `which mail did I receive ${tekst}` : `welke mail heb ik ontvangen ${tekst}`;
  // SL-c: de dag gaat in de vraag ("morgen", "volgende week"); bij Fathom en Drive een onderwerp erachter.
  if (id === "agenda") return en ? `what is in my calendar ${tekst}` : `wat staat er ${tekst} in mijn agenda`;
  if (id === "meetings" || id === "drive") return zinZonderTekst(id, taal) + (tekst ? (en ? ` about ${tekst}` : ` over ${tekst}`) : "");
  // SL-b (besluit Frank 24 oktober 2026): /agent is een eigen vorm, "maak een agent die", en die
  // krijgt in de workerpoort nooit een tijdstip, ook niet als de tekst een dag noemt. /worker blijft
  // de vorm met een mogelijk vast tijdstip.
  if (id === "agent") return en ? `make an agent that ${tekst}` : `maak een agent die ${tekst}`;
  return en ? `make a worker that ${tekst}` : `maak een worker die ${tekst}`;
}

/** De vraag van een leesopdracht zonder tekst erachter. PUUR. "alle open taken" laat TASQ alle
 *  open taken lezen (`tasq.takenkeuze`), en "in TASQ" noemt de bron bij naam. */
export function zinZonderTekst(id, taal = "nl") {
  if (id === "taken") return taal === "en" ? "give all open tasks in TASQ" : "geef alle open taken in TASQ";
  if (id === "mail") return taal === "en" ? "which mail did I receive today" : "welke mail heb ik vandaag ontvangen";
  // SL-c: "meetings" telt niet als naam van Fathom (GL201-1-a) en zou ook de agenda openen, dus de zin
  // noemt Fathom en gespreksverslagen; Fathom en Drive houden hun eigen terugkijkvenster uit het register.
  if (id === "agenda") return taal === "en" ? "what is in my calendar today" : "wat staat er vandaag in mijn agenda";
  if (id === "meetings") return taal === "en" ? "which meeting notes are in Fathom" : "welke gespreksverslagen staan er in Fathom";
  if (id === "drive") return taal === "en" ? "which files are in Google Drive" : "welke bestanden staan er in Google Drive";
  return "";
}

/** De zin uit het formulier van /worker of /agent. PUUR. `velden`: wat, naam, bronnen (ids),
 *  dagen (0 tot 6) en tijd ("07:45"). */
export function zinUitFormulier(id, velden, taal = "nl") {
  const en = taal === "en";
  const wat = (velden.wat || "").trim();
  if (!wat) return "";
  const naam = (velden.naam || "").trim().replace(/["“”]/g, "");
  const bronnen = WORKERBRONNEN.filter((b) => (velden.bronnen || []).includes(b.id)).map((b) => b.woord);
  // GL213-6: ook /agent neemt gekozen dagen en een tijd mee (SL-b van 24 oktober vervalt).
  const dagen = [...new Set(velden.dagen || [])].sort((a, b) => a - b);
  const delen = [id === "agent" ? (en ? "make an agent" : "maak een agent") : (en ? "make a worker" : "maak een worker")];
  if (naam) delen.push(en ? `named "${naam}"` : `genaamd "${naam}"`);
  delen.push(en ? `that ${wat}` : `die ${wat}`);
  if (bronnen.length) delen.push(`${en ? "from" : "uit"} ${bronnen.join(en ? " and " : " en ")}`);
  if (dagen.length) {
    const namen = dagen.map((d) => DAGNAAM[en ? "en" : "nl"][d]);
    delen.push(`${en ? "every" : "elke"} ${namen.join(en ? " and " : " en ")} ${en ? "at" : "om"} ${velden.tijd || "08:00"}`);
  }
  return delen.join(" ");
}
